import React, { useState, useEffect } from 'react';
import { Loader2, Plus, X } from 'lucide-react';
import { motion } from 'framer-motion';
import PageHeader from '../components/PageHeader';
import DataTable from '../components/DataTable';
import ConfirmDialog from '../components/ConfirmDialog';
import { api, productApi } from '../../services/api';
import { useToast } from '../hooks/useToast';
import { coupons as dummyCoupons } from '../data/dummyData';

const Coupons = () => {
  const { success, error: showError, warning } = useToast();
  const [couponList, setCouponList] = useState(dummyCoupons);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [selectedCoupon, setSelectedCoupon] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState({
    code: '',
    discountType: 'percentage',
    discount: '',
    minCartValue: 0,
    maxUses: 999999,
    expiryDate: '',
    description: '',
    applicableProducts: []
  });

  useEffect(() => {
    const loadCoupons = async () => {
      try {
        setLoading(true);
        setError('');

        try {
          const response = await api.get('/coupons');
          const apiCoupons = (response.data?.data || []).map((coupon) => ({
            id: coupon._id,
            code: coupon.code,
            discount: coupon.value || coupon.discount,
            type: coupon.type || (coupon.discountType === 'percentage' ? 'percentage' : 'fixed'),
            used: coupon.usedCount || 0,
            maxUses: coupon.maxUses || 999999,
            expiryDate: new Date(coupon.expiryDate || coupon.expiresAt).toLocaleDateString(),
            status: coupon.status || (new Date(coupon.expiryDate || coupon.expiresAt) > new Date() ? 'active' : 'expired'),
            description: coupon.description || '',
            minCartValue: coupon.minCartValue || 0,
            products: coupon.applicableProducts?.length ? `${coupon.applicableProducts.length} selected` : 'All products',
          }));
          setCouponList(apiCoupons);
        } catch (apiErr) {
          // Fall back to dummy data
          setCouponList(dummyCoupons);
          if (apiErr.response?.status !== 404) {
            warning('Using sample data - API not available');
          }
        }
      } catch (err) {
        const msg = err.response?.data?.message || 'Failed to load coupons';
        setError(msg);
        warning(msg);
      } finally {
        setLoading(false);
      }
    };

    loadCoupons();
  }, [warning]);

  useEffect(() => {
    productApi.list({ limit: 300 })
      .then((response) => setProducts(response.data?.data || []))
      .catch(() => setProducts([]));
  }, []);

  const createCoupon = async (event) => {
    event.preventDefault();
    try {
      const response = await api.post('/coupons', {
        ...form,
        discount: Number(form.discount),
        minCartValue: Number(form.minCartValue || 0),
        maxUses: Number(form.maxUses || 999999),
      });
      const coupon = response.data?.data;
      setCouponList((current) => [{
        id: coupon._id,
        code: coupon.code,
        discount: coupon.value || coupon.discount,
        type: coupon.type || coupon.discountType,
        used: coupon.usedCount || 0,
        maxUses: coupon.maxUses || 999999,
        expiryDate: new Date(coupon.expiryDate || coupon.expiresAt).toLocaleDateString(),
        status: coupon.status || 'active',
        minCartValue: coupon.minCartValue || 0,
        products: coupon.applicableProducts?.length ? `${coupon.applicableProducts.length} selected` : 'All products',
      }, ...current]);
      setForm({ code: '', discountType: 'percentage', discount: '', minCartValue: 0, maxUses: 999999, expiryDate: '', description: '', applicableProducts: [] });
      setShowForm(false);
      success('Coupon created successfully');
    } catch (err) {
      showError(err.response?.data?.message || 'Unable to create coupon');
    }
  };

  const handleDelete = (id) => {
    setSelectedCoupon(id);
    setConfirmDelete(true);
  };

  const confirmDeleteCoupon = async () => {
    try {
      setDeleting(true);
      await api.delete(`/coupons/${selectedCoupon}`);
      setCouponList((current) => current.filter((coupon) => coupon.id !== selectedCoupon));
      success('Coupon deleted successfully');
    } catch (err) {
      const msg = err.response?.data?.message || 'Unable to delete coupon';
      showError(msg);
    } finally {
      setDeleting(false);
    }
  };

  const columns = [
    { 
      key: 'code', 
      label: 'Coupon Code', 
      sortable: true,
      render: (val) => <span className="font-mono font-bold text-amber-600">{val}</span>
    },
    {
      key: 'discount',
      label: 'Discount',
      render: (val, row) => `${val}${row.type === 'percentage' ? '%' : '₹'}`,
      sortable: true,
    },
    {
      key: 'used',
      label: 'Usage',
      render: (val, row) => `${val} / ${row.maxUses === 999999 ? '∞' : row.maxUses}`,
      sortable: false,
    },
    { 
      key: 'minCartValue',
      label: 'Min Cart',
      render: (val) => `₹${Number(val || 0).toLocaleString()}`,
    },
    { key: 'products', label: 'Products', sortable: false },
    { key: 'expiryDate', label: 'Expiry Date', sortable: true },
    {
      key: 'status',
      label: 'Status',
      render: (val) => (
        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
          val === 'active'
            ? 'bg-green-100 text-green-700'
            : 'bg-red-100 text-red-700'
        }`}>
          {val.charAt(0).toUpperCase() + val.slice(1)}
        </span>
      ),
    },
  ];

  return (
    <>
      <div className="space-y-6">
        <PageHeader
          title="Coupons"
          description="Manage discount coupons"
          breadcrumbs={['Home', 'Coupons']}
          action={
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="flex items-center gap-2 bg-amber-600 text-white px-4 py-2 rounded-lg hover:bg-amber-700 transition font-medium"
              onClick={() => setShowForm((value) => !value)}
            >
              <Plus size={18} />
              Add Coupon
            </motion.button>
          }
        />

        {showForm && (
          <>
            <button className="fixed inset-0 bg-black/40 z-40" onClick={() => setShowForm(false)} aria-label="Close coupon drawer" />
            <aside className="fixed inset-y-0 right-0 z-50 w-full max-w-xl bg-white shadow-2xl border-l border-gray-100 overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-100 p-5 flex items-center justify-between">
                <div>
                  <p className="text-xs uppercase tracking-[0.18em] text-amber-600 font-semibold">Coupon Setup</p>
                  <h2 className="text-xl font-bold text-gray-900">Create Coupon</h2>
                </div>
                <button onClick={() => setShowForm(false)} className="p-2 hover:bg-gray-100 rounded-lg" aria-label="Close coupon drawer">
                  <X size={20} />
                </button>
              </div>
              <form onSubmit={createCoupon} className="p-5 grid gap-4">
                <input required value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="Coupon code" className="border border-gray-200 rounded-lg px-4 py-2" />
                <div className="grid grid-cols-2 gap-3">
                  <select value={form.discountType} onChange={(e) => setForm({ ...form, discountType: e.target.value })} className="border border-gray-200 rounded-lg px-4 py-2">
                    <option value="percentage">Percentage discount</option>
                    <option value="flat">Flat discount</option>
                  </select>
                  <input required type="number" value={form.discount} onChange={(e) => setForm({ ...form, discount: e.target.value })} placeholder="Discount value" className="border border-gray-200 rounded-lg px-4 py-2" />
                  <input type="number" value={form.minCartValue} onChange={(e) => setForm({ ...form, minCartValue: e.target.value })} placeholder="Minimum cart value" className="border border-gray-200 rounded-lg px-4 py-2" />
                  <input type="number" value={form.maxUses} onChange={(e) => setForm({ ...form, maxUses: e.target.value })} placeholder="Max uses" className="border border-gray-200 rounded-lg px-4 py-2" />
                </div>
                <input type="date" value={form.expiryDate} onChange={(e) => setForm({ ...form, expiryDate: e.target.value })} className="border border-gray-200 rounded-lg px-4 py-2" />
                <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Description" className="border border-gray-200 rounded-lg px-4 py-2" />
                <div>
                  <p className="text-sm font-semibold text-gray-700 mb-2">Applicable products from database</p>
                  <div className="max-h-80 overflow-auto border border-gray-200 rounded-lg p-3 grid gap-2">
                    <label className="flex items-center gap-2 text-sm text-gray-700 border-b border-gray-100 pb-2">
                      <input type="checkbox" checked={form.applicableProducts.length === 0} onChange={() => setForm({ ...form, applicableProducts: [] })} />
                      All products
                    </label>
                    {products.map((product) => (
                      <label key={product._id} className="flex items-start gap-2 text-sm text-gray-700">
                        <input
                          type="checkbox"
                          checked={form.applicableProducts.includes(product._id)}
                          onChange={(e) => setForm((current) => ({
                            ...current,
                            applicableProducts: e.target.checked
                              ? [...current.applicableProducts, product._id]
                              : current.applicableProducts.filter((id) => id !== product._id)
                          }))}
                        />
                        <span>{product.name}</span>
                      </label>
                    ))}
                    {products.length === 0 && <p className="text-sm text-gray-500">No database products found.</p>}
                  </div>
                </div>
                <button className="bg-amber-600 text-white rounded-lg px-5 py-3 font-semibold">Create Coupon</button>
              </form>
            </aside>
          </>
        )}

        {error && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-sm text-yellow-700">
            {error}
          </div>
        )}

        {loading ? (
          <div className="bg-white rounded-xl border border-gray-100 p-8 flex items-center justify-center">
            <Loader2 className="animate-spin text-amber-600 mr-2" size={20} />
            <span className="text-gray-600">Loading coupons...</span>
          </div>
        ) : (
          <DataTable
            columns={columns}
            data={couponList}
            onDelete={handleDelete}
            searchable={true}
            paginate={true}
            itemsPerPage={10}
          />
        )}
      </div>

      <ConfirmDialog
        isOpen={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={confirmDeleteCoupon}
        title="Delete Coupon"
        message="Are you sure you want to delete this coupon?"
        confirmText={deleting ? "Deleting..." : "Delete"}
        isDangerous={true}
      />
    </>
  );
};

export default Coupons;
